import { buildCoPilotTranscriptTurns } from '@terra/shared-chat-trace';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { downloadUrlAsFile } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import type { CoPilotSessionExportFormat } from './copilot-session-export-formats';
import type { CoPilotTraceExportStatus } from '../../../utils/co-pilot-trace-export';

export class CoPilotSessionExportEmptyError extends Error {
  constructor() {
    super('CoPilotSessionExportEmptyError');
    this.name = 'CoPilotSessionExportEmptyError';
  }
}

const CHAT_HISTORY_NOT_FOUND_MESSAGE = 'Chat history not found';

export function hasCoPilotSessionExportableContent(
  messages: TerraUIMessage[],
): boolean {
  if (messages.length === 0) {
    return false;
  }

  return buildCoPilotTranscriptTurns(messages).length > 0;
}

export function assertCoPilotSessionExportableContent({
  messages,
}: {
  messages: TerraUIMessage[];
}): void {
  if (!hasCoPilotSessionExportableContent(messages)) {
    throw new CoPilotSessionExportEmptyError();
  }
}

export async function downloadCoPilotSessionExport({
  threadId,
  applicationId,
  format,
  status = 'completed',
  chatTitle,
  requestFilename,
  attachmentFilename,
  csvObjectKinds,
  csvSources,
  csvKind,
  markdownKind,
}: {
  threadId: string;
  applicationId: string;
  format: CoPilotSessionExportFormat;
  status?: CoPilotTraceExportStatus;
  chatTitle?: string;
  requestFilename?: string;
  attachmentFilename?: string;
  csvObjectKinds?: string[];
  csvSources?: string[];
  csvKind?: 'transcript' | 'objects' | 'http_exchanges';
  markdownKind?: 'transcript' | 'objects' | 'session' | 'http_exchanges';
}): Promise<void> {
  const { blob, filename } =
    await terraProxyApiClient.fetchCoPilotSessionExport({
      threadId,
      applicationId,
      format,
      status,
      chatTitle,
      requestFilename,
      attachmentFilename,
      csvObjectKinds,
      csvSources,
      csvKind,
      markdownKind,
    });

  const url = URL.createObjectURL(blob);
  try {
    downloadUrlAsFile({ url, filename });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function resolveCoPilotExportErrorMessage({
  error,
  fallback,
  emptyMessage,
}: {
  error: unknown;
  fallback: string;
  emptyMessage?: string;
}): string {
  if (error instanceof CoPilotSessionExportEmptyError) {
    return emptyMessage ?? fallback;
  }

  if (error instanceof Error) {
    const message = error.message.trim();
    if (!message) {
      return fallback;
    }

    if (
      emptyMessage &&
      (message === CHAT_HISTORY_NOT_FOUND_MESSAGE ||
        message === 'No messages in this chat to export yet.')
    ) {
      return emptyMessage;
    }

    return message;
  }

  return fallback;
}
